/**
 * Progressive enhancement de motion para grupos de listagem (checkpoint 1 —
 * sistema global de marca). Marca cada grupo com data-bso-motion e revela
 * os filhos em cascata via IntersectionObserver; assets/brand.css só oculta
 * grupos marcados "ready", então falha de JS ou reduced motion nunca
 * escondem conteúdo — na pior hipótese ele já nasce visível. Grupos já
 * dentro do viewport no momento da preparação vão direto a "visible": como
 * este módulo é deferido, marcá-los "ready" (oculto) depois de já terem
 * pintado causaria um flash visible→hidden→visible.
 */

const SELETOR_GRUPOS = '.resource-list, .product-grid, .blog-posts-container, .footer-content';
const INDICE_MAXIMO = 7;

/** @param {HTMLElement} grupo */
function revelarGrupo(grupo) {
  grupo.dataset.bsoMotion = 'visible';
}

/** @param {HTMLElement} grupo */
function observarGrupo(grupo) {
  const observer = new IntersectionObserver(
    (entradas) => {
      for (const entrada of entradas) {
        if (!entrada.isIntersecting) continue;
        revelarGrupo(/** @type {HTMLElement} */ (entrada.target));
        observer.unobserve(entrada.target);
      }
    },
    { threshold: 0.15, rootMargin: '0px 0px -10% 0px' },
  );

  observer.observe(grupo);
}

/**
 * Prepara os grupos de motion dentro de `raiz` (inclusive a própria raiz,
 * quando ela mesma casa com o seletor).
 * @param {Document | Element} raiz
 */
export function prepararMotion(raiz = document) {
  const grupos =
    raiz instanceof Element && raiz.matches(SELETOR_GRUPOS)
      ? [raiz]
      : Array.from(raiz.querySelectorAll(SELETOR_GRUPOS));

  if (grupos.length === 0) return;

  const semObserver = !('IntersectionObserver' in window);
  const reduzMovimento = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  for (const grupo of grupos) {
    if (grupo.dataset.bsoMotion === 'visible') continue;

    Array.from(grupo.children).forEach((filho, indice) => {
      /** @type {HTMLElement} */ (filho).style.setProperty('--bso-index', String(Math.min(indice, INDICE_MAXIMO)));
    });

    const rect = grupo.getBoundingClientRect();
    const jaNaViewport = rect.top < window.innerHeight && rect.bottom > 0;

    if (semObserver || reduzMovimento || jaNaViewport) {
      revelarGrupo(grupo);
      continue;
    }

    grupo.dataset.bsoMotion = 'ready';
    observarGrupo(grupo);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => prepararMotion());
} else {
  prepararMotion();
}

document.addEventListener('shopify:section:load', (event) => {
  prepararMotion(/** @type {HTMLElement} */ (event.target));
});
